import {
  AlertTriangle,
  BadgeCheck,
  Banknote,
  Blocks,
  Braces,
  Building2,
  ClipboardCheck,
  FileCheck2,
  Gauge,
  KeyRound,
  LucideIcon,
  Network,
  ReceiptText,
  Route,
  ShieldCheck,
  ShieldQuestion,
  Stethoscope,
} from 'lucide-react';

export type SectionId =
  | 'launch'
  | 'authority'
  | 'approvals'
  | 'onboarding'
  | 'ehr'
  | 'payer'
  | 'claims'
  | 'payments'
  | 'denials'
  | 'audit'
  | 'developer';

export type StatusTone = 'complete' | 'ready' | 'action' | 'blocked' | 'neutral';

export interface NavSection {
  id: SectionId;
  label: string;
  description: string;
  icon: LucideIcon;
}

export interface Gate {
  label: string;
  state: string;
  tone: StatusTone;
  proof: string;
}

export interface Workstream {
  id: SectionId;
  title: string;
  owner: string;
  summary: string;
  primaryAction: string;
  secondaryAction: string;
  icon: LucideIcon;
  metrics: Array<{ label: string; value: string; tone?: StatusTone }>;
  steps: Array<{ label: string; state: string; detail: string; tone: StatusTone }>;
}

export interface AuthorityWorkaround {
  id: string;
  authority: string;
  cannotBypass: string;
  vaerionOwnedPath: string;
  proofReceipt: string;
  nextAction: string;
  tone: StatusTone;
}

export interface CarrierCertification {
  id: string;
  carrier: string;
  market: string;
  routeType: string;
  routeStatus: string;
  tone: StatusTone;
  transactions: string[];
  companionGuide: string;
  testPackets: string;
  acknowledgements: string;
  eraLifecycle: string;
  certificationReceipt: string;
  productionGate: string;
  cutoverDecision: string;
  owner: string;
  nextAction: string;
  proves: string;
  doesNotProve: string;
}

export const navSections: NavSection[] = [
  { id: 'launch', label: 'Launch Control', description: 'One place to see cutover evidence.', icon: Gauge },
  { id: 'authority', label: 'Authority Lab', description: 'No-signature proof alternatives.', icon: ShieldQuestion },
  { id: 'approvals', label: 'Approval Packets', description: 'Payer, EHR, hospital, finance packets.', icon: FileCheck2 },
  { id: 'onboarding', label: 'Onboarding', description: 'Tenant, facilities, providers, roles.', icon: Building2 },
  { id: 'ehr', label: 'EHR / FHIR', description: 'Epic-first conformance and mapping.', icon: Stethoscope },
  { id: 'payer', label: 'Carrier Matrix', description: 'Payer-by-payer route proof.', icon: Route },
  { id: 'claims', label: 'Claim Intake', description: 'Validate 837 and claim API payloads.', icon: ClipboardCheck },
  { id: 'payments', label: 'Payments', description: 'ERA, posting, refunds, deposits.', icon: Banknote },
  { id: 'denials', label: 'Denials', description: 'Appeal packets and learning loop.', icon: AlertTriangle },
  { id: 'audit', label: 'Audit Proof', description: 'Plain-English evidence exports.', icon: FileCheck2 },
  { id: 'developer', label: 'Developer', description: 'API keys, webhooks, payload schemas.', icon: Braces },
];

export const launchGates: Gate[] = [
  {
    label: 'VAERION-owned readiness',
    state: 'Ready for partner review',
    tone: 'complete',
    proof: 'Internal billing, EHR, payer-route, payment, audit, and pilot proof packets are available.',
  },
  {
    label: 'Counterparty authority',
    state: 'Required before live billing',
    tone: 'blocked',
    proof: 'Payer trading partner approval, Epic/customer credentials, and bank/payment authority must be recorded.',
  },
  {
    label: 'Partner implementation',
    state: 'Guided setup in progress',
    tone: 'action',
    proof: 'Facility, provider, role, payer, EHR, and webhook setup steps are shown with owners.',
  },
];

export const workstreams: Workstream[] = [
  {
    id: 'launch',
    title: 'Launch Control',
    owner: 'Executive operator',
    summary: 'Show the receipt-level evidence VAERION controls today and the counterparty authority required for production cutover.',
    primaryAction: 'Generate launch packet',
    secondaryAction: 'Run launch workflow',
    icon: Gauge,
    metrics: [
      { label: 'VAERION-owned infra', value: 'Ready', tone: 'complete' },
      { label: 'Synthetic pilot', value: 'Runnable', tone: 'ready' },
      { label: 'Counterparty cutover', value: 'Authority required', tone: 'blocked' },
    ],
    steps: [
      { label: 'Internal launch proof', state: 'Ready', detail: 'Billing portal, edge guardrails, AWS PHI plane, and proof packet surfaces are in place.', tone: 'complete' },
      { label: 'Authority workarounds', state: 'Available', detail: 'Sandbox, synthetic, de-identified, packet, and replay paths can be run without signatures.', tone: 'ready' },
      { label: 'Production authority', state: 'Counterparty evidence required', detail: 'Live payer, EHR, payment, and hospital cutover remain gated until counterparty evidence exists.', tone: 'blocked' },
    ],
  },
  {
    id: 'authority',
    title: 'Authority Workaround Lab',
    owner: 'Implementation command',
    summary: 'Convert every external dependency into a VAERION-owned proof path without claiming live authority before it exists.',
    primaryAction: 'Run synthetic authority suite',
    secondaryAction: 'Export authority matrix',
    icon: ShieldQuestion,
    metrics: [
      { label: 'Bypass attempts', value: '0', tone: 'complete' },
      { label: 'Proof paths', value: '5', tone: 'ready' },
      { label: 'Live cutover', value: 'Authority required', tone: 'blocked' },
    ],
    steps: [
      { label: 'Replace waiting with proof', state: 'Available now', detail: 'VAERION can run sandbox, synthetic, de-identified, packet, and replay tests before signatures arrive.', tone: 'complete' },
      { label: 'Pre-negotiate the evidence', state: 'Ready', detail: 'Each external gate gets a packet that shows exactly what was tested and what still requires authority.', tone: 'ready' },
      { label: 'No false production claims', state: 'Enforced', detail: 'No payer transmission, Epic write, money movement, or hospital PHI access is unlocked by a simulation receipt.', tone: 'blocked' },
    ],
  },
  {
    id: 'approvals',
    title: 'Counterparty Approval Packet Factory',
    owner: 'Launch authority team',
    summary: 'Prepare the exact packets hospitals, payers, Epic/customer IT, and finance teams need to issue production authority.',
    primaryAction: 'Prepare payer packet',
    secondaryAction: 'Prepare all packets',
    icon: FileCheck2,
    metrics: [
      { label: 'Packet templates', value: '4', tone: 'ready' },
      { label: 'VAERION prefill', value: 'Ready', tone: 'complete' },
      { label: 'External submission', value: 'User controlled', tone: 'blocked' },
    ],
    steps: [
      { label: 'Packet factory', state: 'Ready', detail: 'VAERION can generate non-PHI payer, Epic/customer, hospital, and financial authority packets for counterparty review.', tone: 'complete' },
      { label: 'Evidence checklist', state: 'Ready', detail: 'Each packet names the issuer, scope, artifacts, proof receipts, and fields the counterparty must provide.', tone: 'ready' },
      { label: 'External approval', state: 'Counterparty controlled', detail: 'VAERION does not submit, sign, or self-approve these packets; it prepares them and records returned evidence.', tone: 'blocked' },
    ],
  },
  {
    id: 'onboarding',
    title: 'Partner Onboarding',
    owner: 'Implementation lead',
    summary: 'Collect the tenant, facility, provider, payer, and role facts VAERION needs before pilot traffic.',
    primaryAction: 'Create onboarding packet',
    secondaryAction: 'Import roster',
    icon: Building2,
    metrics: [
      { label: 'Facilities', value: '3' },
      { label: 'Providers', value: '42' },
      { label: 'Roles mapped', value: '8' },
    ],
    steps: [
      { label: 'Tenant profile', state: 'Ready', detail: 'BAA boundary, legal name, and implementation owner recorded.', tone: 'complete' },
      { label: 'Facility roster', state: 'Ready for import', detail: 'CSV validator checks NPI, TIN, place of service, and billing address.', tone: 'action' },
      { label: 'User roles', state: 'Review', detail: 'Partner admins assign billing, compliance, implementation, and read-only access.', tone: 'ready' },
    ],
  },
  {
    id: 'ehr',
    title: 'EHR / FHIR Conformance',
    owner: 'Integration engineer',
    summary: 'Prove sandbox discovery, SMART/OIDC, Patient, Coverage, Encounter, and DocumentReference behavior.',
    primaryAction: 'Run EHR lab',
    secondaryAction: 'Review mapping',
    icon: Network,
    metrics: [
      { label: 'FHIR version', value: 'R4' },
      { label: 'Sandbox OAuth', value: 'Not run yet', tone: 'action' },
      { label: 'Production writes', value: 'Authority required', tone: 'blocked' },
    ],
    steps: [
      { label: 'CapabilityStatement', state: 'Passed', detail: 'Public Epic sandbox discovery can be parsed and receipt-stored.', tone: 'complete' },
      { label: 'Mapping validation', state: 'Simulated', detail: 'Synthetic hospital fixtures cover patient, coverage, encounter, and document references.', tone: 'ready' },
      { label: 'Production credentials', state: 'Counterparty authority', detail: 'Requires Epic/customer approval and credential evidence before real patient access.', tone: 'blocked' },
    ],
  },
  {
    id: 'payer',
    title: 'Carrier Certification Matrix',
    owner: 'Payer operations',
    summary: 'Track each carrier route from companion guide to 837 test packet, 999/277CA/835 receipts, certification evidence, and live cutover gate.',
    primaryAction: 'Open carrier matrix',
    secondaryAction: 'Run route proof',
    icon: Route,
    metrics: [
      { label: 'Carriers tracked', value: '6' },
      { label: 'Transaction families', value: '7' },
      { label: 'Production routes', value: 'Payer authority required', tone: 'blocked' },
    ],
    steps: [
      { label: 'Companion guide', state: 'Per-carrier', detail: 'Each payer route records which guide/version was used and which transactions it governs.', tone: 'action' },
      { label: 'X12 test packet', state: 'Internally provable', detail: '837, acknowledgement, and ERA simulations can be generated and tied to a route proof receipt.', tone: 'complete' },
      { label: 'Trading partner approval', state: 'Payer authority', detail: 'Carrier-issued certification acceptance is required before any live production transmission.', tone: 'blocked' },
    ],
  },
  {
    id: 'claims',
    title: 'Claim Intake And Validation',
    owner: 'Billing operations',
    summary: 'Validate claim payloads, service lines, member facts, coding evidence, and denial probability before submission.',
    primaryAction: 'Validate sample claim',
    secondaryAction: 'Open API schema',
    icon: ClipboardCheck,
    metrics: [
      { label: 'Readiness stages', value: '13' },
      { label: 'NCCI + MUE enforcement', value: 'Active', tone: 'complete' },
      { label: 'Live submit', value: 'Guarded', tone: 'blocked' },
    ],
    steps: [
      { label: 'Required facts', state: 'Checked', detail: 'Member ID, payer, provider NPI, CPT/HCPCS, diagnosis, modifiers, CMS MUE units, and charge are validated.', tone: 'complete' },
      { label: 'Denial prevention', state: 'Native', detail: 'Claim scoring, payer intelligence, and correction rules run before route selection.', tone: 'complete' },
      { label: 'Production send', state: 'Payer authority', detail: 'Submission remains fail-closed until payer route authority is recorded.', tone: 'blocked' },
    ],
  },
  {
    id: 'payments',
    title: 'Payment And ERA Reconciliation',
    owner: 'Finance operations',
    summary: 'Post ERA, detect duplicate remittance, reconcile deposits, and prove refunds, voids, and reversals.',
    primaryAction: 'Run payment simulation',
    secondaryAction: 'Review ERA rules',
    icon: Banknote,
    metrics: [
      { label: 'ERA posting', value: 'Ready' },
      { label: 'Duplicate guard', value: 'On' },
      { label: 'Money movement', value: 'Treasury authority required', tone: 'blocked' },
    ],
    steps: [
      { label: '835 parsing', state: 'Ready', detail: 'ERA receipt, CARC/RARC, payment, adjustment, and denial context are retained.', tone: 'complete' },
      { label: 'Deposit reconciliation', state: 'Simulated', detail: 'Bank deposit references can be matched without moving funds.', tone: 'ready' },
      { label: 'Settlement authority', state: 'Financial authority', detail: 'Bank or processor evidence is required for real money movement.', tone: 'blocked' },
    ],
  },
  {
    id: 'denials',
    title: 'Denial And Appeal Learning',
    owner: 'RCM lead',
    summary: 'Separate correction-ready denials from appeal work, then feed payer learning back into future routing.',
    primaryAction: 'Generate appeal packet',
    secondaryAction: 'Review learning',
    icon: AlertTriangle,
    metrics: [
      { label: 'Appeal packet', value: 'Draftable' },
      { label: 'Learning loop', value: 'Active' },
      { label: 'External filing', value: 'Client approval required', tone: 'blocked' },
    ],
    steps: [
      { label: 'Correction path', state: 'Classified', detail: 'VAERION distinguishes source facts, payer pattern, deadline, and appeal work.', tone: 'complete' },
      { label: 'Appeal content', state: 'Prepared', detail: 'Drafts use claim, policy, patient, provider, and evidence context.', tone: 'ready' },
      { label: 'External submission', state: 'Client approval', detail: 'No external appeal filing occurs without recorded authority.', tone: 'blocked' },
    ],
  },
  {
    id: 'audit',
    title: 'Audit Proof Center',
    owner: 'Compliance',
    summary: 'Explain who acted, what changed, why it changed, what evidence exists, and which counterparty authority evidence remains.',
    primaryAction: 'Export proof packet',
    secondaryAction: 'Review ledger',
    icon: ShieldCheck,
    metrics: [
      { label: 'Ledger', value: 'Merkle chained' },
      { label: 'PHI body copied', value: 'No', tone: 'complete' },
      { label: 'Export', value: 'Ready' },
    ],
    steps: [
      { label: 'Plain-English trail', state: 'Ready', detail: 'Events are translated into accountable action summaries.', tone: 'complete' },
      { label: 'Proof receipts', state: 'Attached', detail: 'Each launch action can carry evidence, source, reviewer, and downstream effect.', tone: 'complete' },
      { label: 'Counterparty acceptance', state: 'Separated', detail: 'Internal evidence never masquerades as payer, Epic, bank, or hospital approval.', tone: 'blocked' },
    ],
  },
  {
    id: 'developer',
    title: 'Developer Integration',
    owner: 'Partner engineering',
    summary: 'Give partner engineers a clear implementation path for claims, webhooks, FHIR, and proof exports.',
    primaryAction: 'Create sandbox key',
    secondaryAction: 'Replay webhook',
    icon: KeyRound,
    metrics: [
      { label: 'API surfaces', value: '23' },
      { label: 'Webhooks', value: '6' },
      { label: 'Secrets displayed', value: 'Never', tone: 'complete' },
    ],
    steps: [
      { label: 'Sandbox setup', state: 'Ready', detail: 'Reference IDs and key status are shown without exposing secret material.', tone: 'complete' },
      { label: 'Webhook testing', state: 'Available', detail: 'Claim, payer, ERA, denial, audit, and launch events are queued for delivery.', tone: 'ready' },
      { label: 'Production keys', state: 'Customer approval', detail: 'Production credentials require customer approval and implementation signoff.', tone: 'blocked' },
    ],
  },
];

export const implementationApi = [
  'GET /api/billing/isolation',
  'GET /api/billing/enterprise-readiness',
  'GET /api/billing/approval-excellence',
  'GET /api/billing/enterprise-proof-verifier',
  'GET /api/billing/launch-dossier',
  'GET /api/billing/authority-workarounds',
  'POST /api/billing/authority-workarounds/run',
  'POST /api/billing/authority-evidence/intake',
  'POST /api/billing/enterprise-deployment/surface/run',
  'POST /api/billing/ehr/epic-sandbox/run',
  'POST /api/billing/ehr/epic-sandbox/token-test',
  'POST /api/billing/claims/intake',
  'POST /api/billing/payments/end-to-end-proof/run',
  'POST /api/billing/payments/rail-sandbox/run',
  'POST /api/billing/proof-receipts',
  'POST /api/billing/proof-packets',
  'POST /api/billing/proof-packets/pdf',
  'GET /api/billing/proof-packets',
  'GET /api/billing/proof-packets/:packet_id',
  'POST /api/billing/proof-review',
  'POST /api/billing/developer/api-keys/rotate',
  'POST /api/billing/developer/webhooks/replay',
  'POST /api/billing/payer/companion-guide/validate',
  'POST /api/billing/replay-vault/import',
];

export const proofEvents = [
  { event: 'hospital.pilot_run_recorded', detail: '13-stage revenue-loop pilot packet recorded.' },
  { event: 'billing.certification_suite_recorded', detail: '837, acknowledgements, ERA, and denial proof simulated.' },
  { event: 'hospital.ehr_conformance.epic_sandbox_token_test_recorded', detail: 'Epic sandbox backend OAuth token acceptance recorded.' },
  { event: 'hospital.direct_payer_lifecycle_proof_recorded', detail: '837, 999, 277CA, and 835 proof slots recorded.' },
  { event: 'billing.payment_end_to_end_proof_recorded', detail: 'Synthetic 837, 999/277CA, 835, posting, deposit, duplicate, refund, void, and reversal proof recorded.' },
  { event: 'billing.payment_rail_sandbox_recorded', detail: 'NACHA-style validation, bank-response emulation, de-identified replay, deposit ledger, and accounting controls recorded.' },
  { event: 'billing.authority_workaround_receipt_recorded', detail: 'No-signature proof path recorded without unlocking live external action.' },
];

export const carrierCertificationSummary = [
  { label: 'Carrier authority receipts', value: 'Authority required', tone: 'blocked' as StatusTone },
  { label: 'Internal route proofs', value: '6', tone: 'ready' as StatusTone },
  { label: 'Counterparty acceptances', value: 'Authority required', tone: 'blocked' as StatusTone },
  { label: 'Transactions covered', value: '837P · 837I · 270/271 · 276/277 · 999 · 277CA · 835', tone: 'complete' as StatusTone },
];

export const carrierCertificationMatrix: CarrierCertification[] = [
  {
    id: 'aetna-commercial',
    carrier: 'Aetna Commercial',
    market: 'Commercial professional claims',
    routeType: 'Direct trading-partner route',
    routeStatus: 'Internal packet ready',
    tone: 'action',
    transactions: ['837P', '270/271', '276/277', '999', '277CA', '835'],
    companionGuide: 'Guide slot ready; version must be attached by payer operations.',
    testPackets: 'Synthetic 837P packet can be generated with non-PHI facility, provider, member, diagnosis, and service-line fixtures.',
    acknowledgements: '999 and 277CA receipt lifecycle simulated; carrier-issued receipts not recorded yet.',
    eraLifecycle: '835 posting and duplicate-remittance guard simulated; live ERA enrollment not recorded.',
    certificationReceipt: 'Awaiting carrier-issued test acceptance receipt.',
    productionGate: 'Authority-gated until Aetna confirms trading-partner test approval and production endpoint authority.',
    cutoverDecision: 'Do not send live claims.',
    owner: 'Payer operations',
    nextAction: 'Attach Aetna companion guide, generate the 837P packet, then record payer test-cycle acceptance when issued.',
    proves: 'VAERION can prepare and validate the route packet, required transactions, and acknowledgement workflow.',
    doesNotProve: 'It does not prove Aetna production acceptance or guarantee claim payment.',
  },
  {
    id: 'uhc-commercial',
    carrier: 'UnitedHealthcare Commercial',
    market: 'Commercial professional and institutional claims',
    routeType: 'Direct route candidate',
    routeStatus: 'Guide review required',
    tone: 'action',
    transactions: ['837P', '837I', '270/271', '276/277', '999', '277CA', '835'],
    companionGuide: 'Required before packet is marked internally ready.',
    testPackets: '837P/837I packet factory ready; transaction-specific edits remain guide-dependent.',
    acknowledgements: 'Acknowledgement parser ready; UHC receipt examples not recorded.',
    eraLifecycle: '835 reconciliation simulation ready.',
    certificationReceipt: 'Not recorded.',
    productionGate: 'Authority-gated until UHC guide, enrollment, testing, and route approval evidence exist.',
    cutoverDecision: 'Prepare only; no live send.',
    owner: 'Payer operations',
    nextAction: 'Record UHC guide version and transaction set requirements, then run the packet factory.',
    proves: 'VAERION can stage the full route and identify exactly which guide facts still require payer source evidence.',
    doesNotProve: 'Counterparty payer enrollment evidence has not been issued for this route.',
  },
  {
    id: 'bcbs-state',
    carrier: 'Blue Cross Blue Shield',
    market: 'State-specific commercial plans',
    routeType: 'State affiliate route registry',
    routeStatus: 'State route selection needed',
    tone: 'action',
    transactions: ['837P', '270/271', '999', '277CA', '835'],
    companionGuide: 'Must be selected per state affiliate; generic BCBS certainty is not claimed.',
    testPackets: 'Template ready once state affiliate, payer ID, and guide are selected.',
    acknowledgements: '999/277CA simulation ready; affiliate-specific receipt proof awaiting payer evidence.',
    eraLifecycle: '835 posting simulation ready; affiliate enrollment proof awaiting payer evidence.',
    certificationReceipt: 'Awaiting affiliate-specific acceptance.',
    productionGate: 'Authority-gated until the exact BCBS affiliate route is certified.',
    cutoverDecision: 'No global BCBS route.',
    owner: 'Implementation lead',
    nextAction: 'Choose the affiliate and payer ID before generating the test packet.',
    proves: 'VAERION prevents overclaiming plan-specific or affiliate-specific certainty.',
    doesNotProve: 'Counterparty affiliate-specific production acceptance has not been issued.',
  },
  {
    id: 'medicare-mac',
    carrier: 'Medicare MAC',
    market: 'Medicare fee-for-service',
    routeType: 'MAC enrollment route',
    routeStatus: 'Enrollment evidence required',
    tone: 'blocked',
    transactions: ['837P', '837I', '276/277', '999', '277CA', '835'],
    companionGuide: 'CMS/MAC guide evidence slot ready.',
    testPackets: 'Medicare packet factory ready for non-PHI fixtures; provider enrollment facts must be source-proven.',
    acknowledgements: '999/277CA receipt model ready.',
    eraLifecycle: '835 ERA posting model ready; EFT/ERA enrollment proof awaiting authority evidence.',
    certificationReceipt: 'Not recorded.',
    productionGate: 'Authority-gated until MAC enrollment, submitter ID, test approval, and production authority are recorded.',
    cutoverDecision: 'Authority required.',
    owner: 'Compliance and payer operations',
    nextAction: 'Record MAC jurisdiction, submitter enrollment evidence, and test approval receipt.',
    proves: 'VAERION can enforce Medicare route gates and source-proof requirements.',
    doesNotProve: 'Counterparty CMS/MAC enrollment and production approval have not been issued.',
  },
  {
    id: 'medicaid-state',
    carrier: 'State Medicaid',
    market: 'State Medicaid plans',
    routeType: 'State-specific enrollment route',
    routeStatus: 'State program required',
    tone: 'blocked',
    transactions: ['837P', '837I', '270/271', '999', '277CA', '835'],
    companionGuide: 'Must be attached per state Medicaid program.',
    testPackets: 'Packet generator ready after state program, payer ID, taxonomy, and provider enrollment facts are recorded.',
    acknowledgements: 'Acknowledgement lifecycle ready; state receipt proof awaiting payer evidence.',
    eraLifecycle: '835 matching ready; state ERA enrollment evidence awaiting authority receipt.',
    certificationReceipt: 'Not recorded.',
    productionGate: 'Authority-gated until state Medicaid enrollment and testing authority exist.',
    cutoverDecision: 'Authority required.',
    owner: 'Implementation lead',
    nextAction: 'Select state Medicaid program and record provider enrollment evidence.',
    proves: 'VAERION separates internal technical readiness from state Medicaid authority.',
    doesNotProve: 'Counterparty state-program production acceptance has not been issued.',
  },
  {
    id: 'cigna-commercial',
    carrier: 'Cigna Commercial',
    market: 'Commercial professional claims',
    routeType: 'Direct route candidate',
    routeStatus: 'Pre-certification workspace',
    tone: 'ready',
    transactions: ['837P', '270/271', '276/277', '999', '277CA', '835'],
    companionGuide: 'Guide evidence slot ready.',
    testPackets: 'Synthetic 837P and acknowledgement lifecycle ready to run.',
    acknowledgements: '999/277CA simulation ready; carrier-issued receipts not recorded.',
    eraLifecycle: '835 posting simulation ready.',
    certificationReceipt: 'Not recorded.',
    productionGate: 'Authority-gated until guide, test cycle, and production route approval are recorded.',
    cutoverDecision: 'Internal preparation only.',
    owner: 'Payer operations',
    nextAction: 'Generate internal packet and produce route proof for partner review.',
    proves: 'VAERION can assemble a carrier-ready evidence packet before Cigna approval.',
    doesNotProve: 'Counterparty Cigna live-submission approval has not been issued.',
  },
];

export const readinessBadges = [
  { label: 'PHI-safe AI routing', icon: ShieldCheck },
  { label: 'NCCI + MUE + OIG checks', icon: BadgeCheck },
  { label: 'X12 proof lifecycle', icon: ReceiptText },
  { label: 'Counterparty authority gates', icon: Blocks },
];

export const authorityWorkarounds: AuthorityWorkaround[] = [
  {
    id: 'ehr-sandbox',
    authority: 'Epic / customer EHR production access',
    cannotBypass: 'Production SMART launch, real Patient/Coverage/Encounter reads, and DocumentReference writes require customer-approved Epic credentials.',
    vaerionOwnedPath: 'Run Epic public discovery, SMART/OIDC launch simulation, FHIR R4 validator, synthetic hospital fixtures, and de-identified replay vault tests.',
    proofReceipt: 'FHIR capability receipt, mapping receipt, synthetic patient coverage receipt, replay receipt, credential-gap receipt.',
    nextAction: 'Create an Epic readiness packet the partner can hand to its EHR team before credential approval.',
    tone: 'ready',
  },
  {
    id: 'payer-direct',
    authority: 'Payer production trading-partner route',
    cannotBypass: 'Live 837 submission and payer acceptance require payer enrollment, companion-guide compliance, test-cycle acceptance, and production route approval.',
    vaerionOwnedPath: 'Generate payer-specific 837 test packets, validate X12 envelopes, emulate 999/277CA/835 lifecycle, and record companion-guide evidence.',
    proofReceipt: '837 validation receipt, acknowledgement lifecycle receipt, route-readiness receipt, authority-evidence receipt.',
    nextAction: 'Prepare the payer certification packet and keep live transport fail-closed.',
    tone: 'action',
  },
  {
    id: 'payment-rails',
    authority: 'Bank, EFT, ACH, lockbox, or processor authority',
    cannotBypass: 'Real settlement, debit, credit, refund, reversal, or void operations require bank or processor authority.',
    vaerionOwnedPath: 'Run NACHA-style file linting, ERA-to-deposit reconciliation simulation, duplicate remittance protection, refund/void dry-runs, and audit proof.',
    proofReceipt: 'Deposit-match receipt, refund simulation receipt, duplicate-payment guard receipt, money-movement-block receipt.',
    nextAction: 'Give finance a payment-readiness packet while preserving a no-money-movement boundary.',
    tone: 'ready',
  },
  {
    id: 'hospital-security',
    authority: 'Hospital tenant approval and BAA/security review',
    cannotBypass: 'Hospital PHI access and production deployment require signed authority, tenant approval, security review, and implementation owners.',
    vaerionOwnedPath: 'Provision isolated tenant config, role matrix, audit export, incident runbook, BAA boundary checklist, and synthetic end-to-end pilot.',
    proofReceipt: 'Tenant isolation receipt, RBAC receipt, audit receipt, incident-readiness receipt, pilot-script receipt.',
    nextAction: 'Export the hospital implementation dossier for procurement, security, and revenue-cycle review.',
    tone: 'complete',
  },
  {
    id: 'clinical-billing-proof',
    authority: 'Real patient billing production cutover',
    cannotBypass: 'A full production cutover still requires all external authority proofs to be accepted by the relevant counterparty.',
    vaerionOwnedPath: 'Run the complete synthetic loop: pre-visit, eligibility, auth, claim, payer test lifecycle, ERA, posting, denial, appeal packet, audit export.',
    proofReceipt: 'End-to-end pilot receipt with explicit internal-ready versus external-approved separation.',
    nextAction: 'Use the packet to prove system behavior without representing it as live payer/EHR/bank approval.',
    tone: 'blocked',
  },
];
